import React, { useMemo, useState } from "react";
import moment from "moment";
import DataTableComponentFrontPagination from "../../common/DataTableComponentFrontPagination";
import { TABLE_DATE_FORMAT } from "../../../utils/Constants";
import { EVENT_TYPE_LABELS } from "./ohsConstants";
import { MutedText, SectionCard, SectionHeader, SectionTitle } from "./Ohs.styles";
import { getItemStatuses } from "./ohsUtils";
import ConditionBadges from "./ConditionBadges";

/**
 * Read-only view of what's in a box right now - each item's latest recorded quantity, expiry and
 * statuses, and the check or QR usage it came from. Nothing here is editable; a check updates it.
 * @param {Object} props
 * @param {Array<Object>} props.currentStock Box's currentStock rows from getBox.
 */
function BoxCurrentStock({ currentStock }) {
    const [search, setSearch] = useState("");

    const filteredStock = useMemo(() => {
        const searchText = search.trim().toLowerCase();
        if (!searchText) {
            return currentStock;
        }
        return currentStock.filter((stockRow) => stockRow.item_name.toLowerCase().includes(searchText));
    }, [currentStock, search]);

    const columns = useMemo(
        () => [
            {
                name: "Item",
                selector: "item_name",
                sortable: true,
                grow: 2,
                minWidth: "220px",
            },
            {
                name: "Quantity",
                selector: "quantity",
                sortable: true,
                minWidth: "120px",
                cell: (row) => `${row.quantity} of ${row.expected_quantity} ${row.unit}`,
            },
            {
                name: "Expiry Date",
                selector: "expiry_date",
                sortable: true,
                minWidth: "130px",
                cell: (row) => (row.expiry_date ? moment(row.expiry_date).format(TABLE_DATE_FORMAT) : "-"),
            },
            {
                name: "Status",
                minWidth: "160px",
                cell: (row) => <ConditionBadges statuses={getItemStatuses(row)} />,
            },
            {
                name: "Last Recorded",
                selector: "check_date",
                sortable: true,
                minWidth: "200px",
                cell: (row) => (
                    <div>
                        <div>{moment(row.check_date).format(TABLE_DATE_FORMAT)} · {EVENT_TYPE_LABELS[row.event_type]}</div>
                        <MutedText>{row.checked_by_name}</MutedText>
                    </div>
                ),
            },
            {
                name: "Notes",
                selector: "notes",
                grow: 2,
                minWidth: "180px",
                cell: (row) => row.notes || "-",
            },
        ],
        []
    );

    return (
        <SectionCard>
            <SectionHeader>
                <SectionTitle>Current Stock</SectionTitle>
            </SectionHeader>
            {currentStock.length ? (
                <DataTableComponentFrontPagination
                    data={filteredStock}
                    columns={columns}
                    keyField="box_item_id"
                    search={search}
                    setSearch={setSearch}
                    isExport={false}
                />
            ) : (
                <MutedText>Nothing recorded yet - the box&apos;s first check will fill this in.</MutedText>
            )}
        </SectionCard>
    );
}

export default BoxCurrentStock;
